import React, { useState, useEffect } from 'react';
import { overtimeAPI, usersAPI } from '../../services/api';
import { Clock, CheckCircle, XCircle, AlertCircle, Search, ChevronLeft, ChevronRight, Ban, Plus, Pencil, History } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { formatDate } from '../../utils/attendanceUtils';

const PAGE_SIZE = 25;

const defaultRange = () => {
  const d = new Date();
  const start = new Date(d.getFullYear(), d.getMonth() - 1, 21).toISOString().slice(0, 10);
  const end   = new Date(d.getFullYear(), d.getMonth() + 1, 20).toISOString().slice(0, 10);
  return { start, end };
};

const OvertimeApprovals = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState(defaultRange().start);
  const [endDate, setEndDate] = useState(defaultRange().end);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [actionModal, setActionModal] = useState(null); // { ids: [], action, remark }
  const [submitting, setSubmitting] = useState(false);

  // Edit modal state
  const emptyEditForm = { start_time: '', end_time: '', break_time: 0, reason: '' };
  const [editModal, setEditModal] = useState(null); // overtime record being edited
  const [editForm, setEditForm] = useState(emptyEditForm);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState('');

  // History modal state
  const [historyModal, setHistoryModal] = useState(null); // { record, entries }
  const [historyLoading, setHistoryLoading] = useState(false);

  // Expanded remark rows
  const [deptFilter, setDeptFilter] = useState('');

  // Expanded remark rows
  const [expandedRemarks, setExpandedRemarks] = useState(new Set());
  const toggleRemark = (id) => setExpandedRemarks(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  // Apply OT on behalf state
  const emptyApplyForm = { date: '', start_time: '', end_time: '', break_time: 0, remark: '' };
  const [applyModal, setApplyModal] = useState(false);
  const [applyForm, setApplyForm] = useState(emptyApplyForm);
  const [applyUser, setApplyUser] = useState(null);
  const [applySearch, setApplySearch] = useState('');
  const [applySearchResults, setApplySearchResults] = useState([]);
  const [applySubmitting, setApplySubmitting] = useState(false);
  const [applyError, setApplyError] = useState('');

  useEffect(() => {
    setSearchTerm('');
    setDeptFilter('');
    setCurrentPage(1);
    setSelectedIds(new Set());
    fetchRequests();
  }, [statusFilter]);

  // Reset page and selection when filters change
  useEffect(() => {
    setCurrentPage(1);
    setSelectedIds(new Set());
  }, [searchTerm, startDate, endDate, deptFilter]);

  const fetchRequests = async () => {
    setLoading(true);
    setError('');
    try {
      const params = statusFilter === 'all' ? {} : { status: statusFilter };
      const response = await overtimeAPI.getAllOvertimeRequests(params);
      setRequests(response.data.overtime_requests);
    } catch (err) {
      setError('Failed to fetch overtime requests');
    } finally {
      setLoading(false);
    }
  };

  // Debounced employee search for Apply OT modal
  useEffect(() => {
    if (!applySearch.trim()) { setApplySearchResults([]); return; }
    const timer = setTimeout(() => {
      usersAPI.getUsers({ search: applySearch.trim(), per_page: 20 })
        .then(res => setApplySearchResults(res.data.users || []))
        .catch(() => setApplySearchResults([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [applySearch]);

  const handleApplySubmit = async (e) => {
    e.preventDefault();
    if (!applyUser) { setApplyError('Please select an employee'); return; }
    setApplySubmitting(true);
    setApplyError('');
    try {
      const isOvernight = applyForm.end_time <= applyForm.start_time;
      const endDate = isOvernight
        ? new Date(new Date(applyForm.date).getTime() + 86400000).toISOString().slice(0, 10)
        : applyForm.date;
      await overtimeAPI.requestOvertimeOnBehalf({
        user_id:    applyUser.id,
        date:       applyForm.date,
        start_time: `${applyForm.date} ${applyForm.start_time}:00`,
        end_time:   `${endDate} ${applyForm.end_time}:00`,
        break_time: parseInt(applyForm.break_time, 10),
        remark:     applyForm.remark,
      });
      setApplyModal(false);
      setApplyForm(emptyApplyForm);
      setApplyUser(null);
      setApplySearch('');
      await fetchRequests();
    } catch (err) {
      setApplyError(err.response?.data?.error || 'Failed to apply overtime');
    } finally {
      setApplySubmitting(false);
    }
  };

  const openEditModal = (record) => {
    setEditForm({
      start_time: format(parseISO(record.start_time), "yyyy-MM-dd'T'HH:mm"),
      end_time:   format(parseISO(record.end_time),   "yyyy-MM-dd'T'HH:mm"),
      break_time: record.break_time,
      reason:     '',
    });
    setEditError('');
    setEditModal(record);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditSubmitting(true);
    setEditError('');
    try {
      const updated = await overtimeAPI.updateOvertime(editModal.id, {
        start_time: editForm.start_time.replace('T', ' ') + ':00',
        end_time:   editForm.end_time.replace('T', ' ')   + ':00',
        break_time: parseInt(editForm.break_time, 10),
        reason:     editForm.reason,
      });
      setRequests(prev => prev.map(r => r.id === editModal.id ? { ...r, ...updated.data } : r));
      setEditModal(null);
    } catch (err) {
      setEditError(err.response?.data?.error || 'Failed to update overtime record');
    } finally {
      setEditSubmitting(false);
    }
  };

  const openHistoryModal = async (record) => {
    setHistoryModal({ record, entries: [] });
    setHistoryLoading(true);
    try {
      const res = await overtimeAPI.getOvertimeHistory(record.id);
      setHistoryModal({ record, entries: res.data });
    } catch {
      setHistoryModal({ record, entries: [] });
    } finally {
      setHistoryLoading(false);
    }
  };

  // Unique sorted department list derived from loaded requests
  const departments = [...new Set(requests.map(r => r.department_name).filter(Boolean))].sort();

  // Filtered list
  const filtered = requests.filter(r => {
    if (startDate && r.date < startDate) return false;
    if (endDate && r.date > endDate) return false;
    if (deptFilter && r.department_name !== deptFilter) return false;
    if (searchTerm && !r.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !r.staff_no?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const paginated = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // Pending logic scoped to current page
  const pendingOnPage = paginated.filter(r => r.status === 'pending');
  const allPendingSelected = pendingOnPage.length > 0 && pendingOnPage.every(r => selectedIds.has(r.id));
  const somePendingSelected = pendingOnPage.some(r => selectedIds.has(r.id));

  const toggleSelectAll = () => {
    const next = new Set(selectedIds);
    if (allPendingSelected) {
      pendingOnPage.forEach(r => next.delete(r.id));
    } else {
      pendingOnPage.forEach(r => next.add(r.id));
    }
    setSelectedIds(next);
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const openActionModal = (ids, action) => {
    setActionModal({ ids: Array.isArray(ids) ? ids : [ids], action, remark: '' });
  };

  const handleActionSubmit = async () => {
    if (!actionModal) return;
    setSubmitting(true);
    const statusMap = { approve: 'approved', reject: 'rejected', cancel: 'cancelled' };
    const status = statusMap[actionModal.action];
    try {
      if (actionModal.ids.length === 1) {
        await overtimeAPI.approveOvertime(actionModal.ids[0], status, actionModal.remark);
      } else {
        await overtimeAPI.bulkApproveOvertime(actionModal.ids, status, actionModal.remark);
      }
      setActionModal(null);
      await fetchRequests();
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${actionModal.action} overtime request`);
    } finally {
      setSubmitting(false);
    }
  };

  const calculateDuration = (startTime, endTime, breakTime) => {
    const start = parseISO(startTime);
    const end = parseISO(endTime);
    const durationMs = end.getTime() - start.getTime();
    const durationHours = (durationMs / (1000 * 60 * 60)) - ((breakTime || 0) / 60);
    return Math.max(0, durationHours);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="h-3 w-3 mr-1" />
            Approved
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle className="h-3 w-3 mr-1" />
            Rejected
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <XCircle className="h-3 w-3 mr-1" />
            Cancelled
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <AlertCircle className="h-3 w-3 mr-1" />
            Pending
          </span>
        );
    }
  };

  const isApprove = actionModal?.action === 'approve';
  const isCancel = actionModal?.action === 'cancel';
  const isBulk = actionModal?.ids?.length > 1;

  // Page number pills (same pattern as AttendanceDashboard)
  const pageItems = [...Array(totalPages).keys()]
    .map(i => i + 1)
    .filter(p => p === 1 || p === totalPages || Math.abs(p - safePage) <= 1);

  return (
    <div className="space-y-6">

      {/* Edit OT Modal */}
      {editModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
            <h3 className="text-lg font-medium text-gray-900">
              Edit OT — {editModal.user_name} ({format(parseISO(editModal.date), 'dd MMM yyyy')})
            </h3>

            {editError && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">{editError}</div>
            )}

            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                  <input
                    type="datetime-local"
                    required
                    value={editForm.start_time}
                    onChange={e => setEditForm(f => ({ ...f, start_time: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                  <input
                    type="datetime-local"
                    required
                    value={editForm.end_time}
                    onChange={e => setEditForm(f => ({ ...f, end_time: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Break Time</label>
                <select
                  value={editForm.break_time}
                  onChange={e => setEditForm(f => ({ ...f, break_time: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={0}>No break</option>
                  <option value={30}>30 minutes</option>
                  <option value={45}>45 minutes</option>
                  <option value={60}>60 minutes</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason for correction <span className="text-red-500">*</span></label>
                <textarea
                  rows={3}
                  required
                  value={editForm.reason}
                  onChange={e => setEditForm(f => ({ ...f, reason: e.target.value }))}
                  placeholder="Describe the correction being made…"
                  className="w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-2">
                <button type="button" onClick={() => setEditModal(null)} disabled={editSubmitting}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50">
                  Cancel
                </button>
                <button type="submit" disabled={editSubmitting}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50">
                  {editSubmitting && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />}
                  Save Correction
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* History Modal */}
      {historyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">
                Edit History — {historyModal.record.user_name} ({format(parseISO(historyModal.record.date), 'dd MMM yyyy')})
              </h3>
              <button onClick={() => setHistoryModal(null)} className="text-gray-400 hover:text-gray-600">
                <XCircle className="h-5 w-5" />
              </button>
            </div>

            {historyLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
              </div>
            ) : historyModal.entries.length === 0 ? (
              <p className="text-sm text-gray-500 py-4 text-center">No edits recorded for this OT entry.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Edited By</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Before</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">After</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {historyModal.entries.map(h => (
                      <tr key={h.id}>
                        <td className="px-4 py-2 whitespace-nowrap text-gray-600">
                          {format(parseISO(h.updated_at), 'dd MMM yyyy HH:mm')}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-gray-900">{h.updated_by_name}</td>
                        <td className="px-4 py-2 text-gray-500 whitespace-nowrap">
                          {format(parseISO(h.old_values.start_time), 'HH:mm')}–{format(parseISO(h.old_values.end_time), 'HH:mm')}
                          {h.old_values.break_time > 0 && ` (-${h.old_values.break_time}m)`}
                        </td>
                        <td className="px-4 py-2 text-gray-900 whitespace-nowrap">
                          {format(parseISO(h.new_values.start_time), 'HH:mm')}–{format(parseISO(h.new_values.end_time), 'HH:mm')}
                          {h.new_values.break_time > 0 && ` (-${h.new_values.break_time}m)`}
                        </td>
                        <td className="px-4 py-2 text-gray-700">{h.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Apply OT on behalf modal */}
      {applyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Apply Overtime on Behalf</h3>

            {applyError && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">{applyError}</div>
            )}

            <form onSubmit={handleApplySubmit} className="space-y-4">
              {/* Employee picker */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
                {applyUser ? (
                  <div className="flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                    <span className="text-sm font-medium">{applyUser.name} <span className="text-gray-500">({applyUser.staff_no})</span></span>
                    <button type="button" onClick={() => setApplyUser(null)} className="text-gray-400 hover:text-gray-600">
                      <XCircle className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    <input
                      type="text"
                      value={applySearch}
                      onChange={e => setApplySearch(e.target.value)}
                      placeholder="Search by name or staff no…"
                      className="pl-9 pr-3 py-2 w-full border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                    {applySearchResults.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-52 overflow-y-auto">
                        {applySearchResults.map(u => (
                          <button
                            key={u.id}
                            type="button"
                            onClick={() => { setApplyUser(u); setApplySearch(''); setApplySearchResults([]); }}
                            className="w-full text-left px-4 py-2 text-sm hover:bg-blue-50"
                          >
                            {u.name} <span className="text-gray-500">({u.staff_no})</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  required
                  value={applyForm.date}
                  onChange={e => setApplyForm(f => ({ ...f, date: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Times */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                  <input
                    type="time"
                    step="900"
                    required
                    value={applyForm.start_time}
                    onChange={e => setApplyForm(f => ({ ...f, start_time: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                  <input
                    type="time"
                    step="900"
                    required
                    value={applyForm.end_time}
                    onChange={e => setApplyForm(f => ({ ...f, end_time: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Break time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Break Time</label>
                <select
                  value={applyForm.break_time}
                  onChange={e => setApplyForm(f => ({ ...f, break_time: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={0}>No break</option>
                  <option value={30}>30 minutes</option>
                  <option value={45}>45 minutes</option>
                  <option value={60}>60 minutes</option>
                </select>
              </div>

              {/* Remark */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remark <span className="text-red-500">*</span></label>
                <textarea
                  rows={3}
                  required
                  value={applyForm.remark}
                  onChange={e => setApplyForm(f => ({ ...f, remark: e.target.value }))}
                  placeholder="Reason for overtime…"
                  className="w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setApplyModal(false); setApplyForm(emptyApplyForm); setApplyUser(null); setApplySearch(''); }}
                  disabled={applySubmitting}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={applySubmitting}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  {applySubmitting && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />}
                  Apply OT
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Action Modal */}
      {actionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
            <h3 className="text-lg font-medium text-gray-900">
              {isApprove ? 'Approve' : isCancel ? 'Cancel' : 'Reject'}{isBulk ? ` ${actionModal.ids.length} Requests` : ' Overtime Request'}
            </h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Manager Remark <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <textarea
                rows={3}
                value={actionModal.remark}
                onChange={(e) => setActionModal({ ...actionModal, remark: e.target.value })}
                placeholder={isApprove ? 'Add a note for the employee...' : isCancel ? 'Please provide a reason for cancellation...' : 'Please provide a reason for rejection...'}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
              {isBulk && (
                <p className="mt-1 text-xs text-gray-500">This remark will be applied to all selected requests.</p>
              )}
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setActionModal(null)}
                disabled={submitting}
                className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleActionSubmit}
                disabled={submitting}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white disabled:opacity-50 ${
                  isApprove ? 'bg-green-600 hover:bg-green-700' : isCancel ? 'bg-orange-600 hover:bg-orange-700' : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {submitting && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>}
                {isApprove ? 'Approve' : isCancel ? 'Cancel OT' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="text-lg font-medium text-gray-900 mr-2">Overtime Approvals</h3>

        {/* Date range */}
        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
          className="border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
        <span className="text-gray-400 text-sm">to</span>
        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
          className="border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" />

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={searchTerm}
            onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            placeholder="Search name or staff no…"
            className="pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 w-52"
          />
        </div>

        {/* Department filter */}
        <select
          value={deptFilter}
          onChange={e => setDeptFilter(e.target.value)}
          className="pl-3 pr-10 py-2 text-sm border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md"
        >
          <option value="">All Departments</option>
          {departments.map(d => <option key={d} value={d}>{d}</option>)}
        </select>

        {/* Status filter */}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="pl-3 pr-10 py-2 text-sm border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md"
        >
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="cancelled">Cancelled</option>
          <option value="all">All Statuses</option>
        </select>

        {/* Apply OT button — HR only */}
        <button
          onClick={() => { setApplyModal(true); setApplyError(''); }}
          className="ml-auto inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="h-4 w-4 mr-1" />
          Apply OT
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-center">
          <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
          <span className="text-red-700 text-sm">{error}</span>
        </div>
      )}

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-md px-4 py-3 flex items-center justify-between">
          <span className="text-sm text-blue-800 font-medium">
            {selectedIds.size} request{selectedIds.size !== 1 ? 's' : ''} selected
          </span>
          <div className="flex space-x-2">
            <button
              onClick={() => openActionModal(Array.from(selectedIds), 'approve')}
              className="inline-flex items-center px-3 py-1.5 border border-green-300 text-xs font-medium rounded text-green-700 bg-white hover:bg-green-50"
            >
              <CheckCircle className="h-3.5 w-3.5 mr-1" />
              Approve Selected
            </button>
            <button
              onClick={() => openActionModal(Array.from(selectedIds), 'reject')}
              className="inline-flex items-center px-3 py-1.5 border border-red-300 text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50"
            >
              <XCircle className="h-3.5 w-3.5 mr-1" />
              Reject Selected
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-600 bg-white hover:bg-gray-50"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Requests List */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center border-t border-gray-200">
            <Clock className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No {statusFilter !== 'all' ? statusFilter : ''} requests</h3>
            <p className="mt-1 text-sm text-gray-500">
              There are no overtime requests matching this filter.
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto border-t border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3">
                      {pendingOnPage.length > 0 && (
                        <input
                          type="checkbox"
                          checked={allPendingSelected}
                          ref={el => { if (el) el.indeterminate = somePendingSelected && !allPendingSelected; }}
                          onChange={toggleSelectAll}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                          title="Select all pending on this page"
                        />
                      )}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time Period</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Manager Remark</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Processed By</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginated.map((request) => (
                    <tr key={request.id} className={`hover:bg-gray-50 ${selectedIds.has(request.id) ? 'bg-blue-50' : ''}`}>
                      <td className="px-4 py-4">
                        {request.status === 'pending' && (
                          <input
                            type="checkbox"
                            checked={selectedIds.has(request.id)}
                            onChange={() => toggleSelect(request.id)}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                          />
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{request.user_name}</div>
                        <div className="text-sm text-gray-500">{request.staff_no}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap border-l border-gray-100">
                        <div className="text-sm font-medium text-gray-900">{formatDate(request.date)}</div>
                        <div className="text-xs text-gray-500">{format(parseISO(request.date), 'EEEE')}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 border-l border-gray-100">
                        {format(parseISO(request.start_time), 'HH:mm')} - {format(parseISO(request.end_time), 'HH:mm')}
                        {request.is_overnight && <span className="ml-2 text-xs text-orange-600">(Overnight)</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 border-l border-gray-100">
                        {calculateDuration(request.start_time, request.end_time, request.break_time).toFixed(1)}h
                        {request.break_time > 0 && <span className="ml-2 text-xs text-gray-500">(-{request.break_time}m break)</span>}
                      </td>
                      <td
                        className={`px-6 py-4 text-sm text-gray-900 border-l border-gray-100 cursor-pointer select-none ${expandedRemarks.has(request.id) ? 'whitespace-normal max-w-xs' : 'max-w-xs truncate'}`}
                        title={expandedRemarks.has(request.id) ? undefined : request.remark}
                        onClick={() => toggleRemark(request.id)}
                      >
                        {request.remark}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap border-l border-gray-100">
                        {getStatusBadge(request.status)}
                      </td>
                      <td
                        className={`px-6 py-4 text-sm text-gray-500 border-l border-gray-100 cursor-pointer select-none ${expandedRemarks.has(request.id) ? 'whitespace-normal max-w-xs' : 'max-w-xs truncate'}`}
                        title={expandedRemarks.has(request.id) ? undefined : (request.manager_remark || undefined)}
                        onClick={() => toggleRemark(request.id)}
                      >
                        {request.manager_remark || '--'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm border-l border-gray-100">
                        {request.processed_by_name ? (
                          <>
                            <div className="text-gray-900">{request.processed_by_name}</div>
                            {request.processed_date && (
                              <div className="text-xs text-gray-400">{format(parseISO(request.processed_date), 'dd MMM yyyy')}</div>
                            )}
                          </>
                        ) : (
                          <span className="text-gray-400">--</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium border-l border-gray-100">
                        {request.status === 'pending' && (
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => openActionModal(request.id, 'approve')}
                              className="inline-flex items-center px-2.5 py-1.5 border border-green-300 text-xs font-medium rounded text-green-700 bg-white hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                              title="Approve"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => openActionModal(request.id, 'reject')}
                              className="inline-flex items-center px-2.5 py-1.5 border border-red-300 text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                              title="Reject"
                            >
                              <XCircle className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                        {request.status === 'approved' && (
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => openEditModal(request)}
                              className="inline-flex items-center px-2.5 py-1.5 border border-blue-300 text-xs font-medium rounded text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              title="Edit timing"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => openHistoryModal(request)}
                              className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-600 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
                              title="View edit history"
                            >
                              <History className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => openActionModal(request.id, 'cancel')}
                              className="inline-flex items-center px-2.5 py-1.5 border border-orange-300 text-xs font-medium rounded text-orange-700 bg-white hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                              title="Cancel approved OT"
                            >
                              <Ban className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 text-sm text-gray-600">
                <span>
                  Showing <span className="font-medium">{(safePage - 1) * PAGE_SIZE + 1}</span>–
                  <span className="font-medium">{Math.min(safePage * PAGE_SIZE, filtered.length)}</span> of{' '}
                  <span className="font-medium">{filtered.length}</span>
                  {selectedIds.size > 0 && (
                    <span className="ml-2 text-blue-600">({selectedIds.size} selected)</span>
                  )}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={safePage === 1}
                    className="p-1 rounded hover:bg-gray-100 disabled:opacity-40"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  {pageItems.map((p, i, arr) => (
                    <React.Fragment key={p}>
                      {i > 0 && arr[i - 1] !== p - 1 && <span className="px-1">…</span>}
                      <button
                        onClick={() => setCurrentPage(p)}
                        className={`w-8 h-8 rounded text-sm ${safePage === p ? 'bg-blue-600 text-white' : 'hover:bg-gray-100'}`}
                      >
                        {p}
                      </button>
                    </React.Fragment>
                  ))}
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={safePage === totalPages}
                    className="p-1 rounded hover:bg-gray-100 disabled:opacity-40"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default OvertimeApprovals;
